/**
 * 1.8.5 — the Tech Wizard's calls (api/src/functions/tech-wizard.ts). A ticket's Wizard works on the ticket
 * (`appointmentId`); Tech Support → Tools works on a session of its own (`S-…`, kept for this browser tab).
 */
import { apiFetch } from '@/lib/api';
import type { HelpKey } from '@shared/tech';
import type { WizardStage } from '@shared/manuals';
import type { FollowedStep, FreeHelpView, HeadsUpView, LibraryChecksView, WalkTurn, WizardAnswer, WizardStateView, WizardStep } from '@shared/tech-wizard';

export type WizardTarget = { appointmentId: string } | { session: string };
export type Lang = 'en' | 'es';

const SESSION_KEY = 'abf.wiz185.session';

function newSession(): string {
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `S-${id}`;
}

/** The Tools session for this tab (a reload keeps what was tried; another tab starts its own). */
export function toolsSession(): string {
  try {
    const have = sessionStorage.getItem(SESSION_KEY);
    if (have && /^S-[A-Za-z0-9-]{8,64}$/.test(have)) return have;
    const id = newSession();
    sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return newSession();
  }
}

export const targetQuery = (t: WizardTarget) => ('appointmentId' in t ? `appointmentId=${encodeURIComponent(t.appointmentId)}` : `session=${encodeURIComponent(t.session)}`);
const post = <T,>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });

/** The job poll: never faster than once a second, and never longer than POLL_LIMIT_MS overall. */
export const POLL_MS = 1000;
export const POLL_LIMIT_MS = 120_000;
/** The Wizard did not finish in time; the page says so plainly (wiz188.timeout) and the job is told to stop. */
export class WizardTimeout extends Error {
  constructor() { super('The Wizard took too long to answer.'); this.name = 'WizardTimeout'; }
}

export async function progressPost<T>(path: string, body: unknown, stage?: (s: WizardStage) => void, signal?: AbortSignal, timing: { pollMs?: number; limitMs?: number; now?: () => number } = {}): Promise<T> {
  if (!stage) return post<T>(path, body);
  const started = await apiFetch<{ id: string }>(path, { method: 'POST', body: JSON.stringify(body), headers: { 'x-wizard-progress': '1' }, signal });
  // During a rolling deployment the preceding API may still answer synchronously.
  if (!started.id) { stage('done'); return started as unknown as T; }
  const pollMs = Math.max(POLL_MS, timing.pollMs ?? POLL_MS);
  const limitMs = timing.limitMs ?? POLL_LIMIT_MS;
  const now = timing.now ?? (() => Date.now());
  const until = now() + limitMs;
  const seen = new Set<WizardStage>();
  const stopped = () => { void post(`/api/tech-wizard/jobs/${started.id}`, {}).catch(() => undefined); };
  signal?.addEventListener('abort', stopped, { once: true });
  try {
    for (;;) {
      signal?.throwIfAborted();
      const job = await apiFetch<{ stages: WizardStage[]; state: string; result?: T }>(`/api/tech-wizard/jobs/${started.id}`, { signal });
      for (const s of job.stages) if (!seen.has(s)) { seen.add(s); stage(s); }
      if (job.state === 'done' && job.result) return job.result;
      if (job.state === 'stopped' || job.state === 'error') throw new Error(job.state);
      if (now() + pollMs > until) { stopped(); throw new WizardTimeout(); }
      await new Promise<void>((r) => setTimeout(r, pollMs));
    }
  } finally { signal?.removeEventListener('abort', stopped); }
}

export const wizardApi = {
  state: (t: WizardTarget) => apiFetch<WizardStateView>(`/api/tech-wizard/state?${targetQuery(t)}`),
  ask: (t: WizardTarget, lang: Lang, question: string, stage?: (s: WizardStage) => void, signal?: AbortSignal, options?: { ownKnowledge?: boolean; misread?: boolean; selectedSection?: string }) => progressPost<WizardAnswer>('/api/tech-wizard/ask', { ...t, lang, question, ...options }, stage, signal),
  walk: (t: WizardTarget, lang: Lang, step: unknown, stage?: (s: WizardStage) => void, signal?: AbortSignal) => progressPost<WalkTurn>('/api/tech-wizard/walk', { ...t, lang, step }, stage, signal),
  tried: (t: WizardTarget, lang: Lang, steps: Pick<WizardStep, 'key' | 'text'>[], stage?: (s: WizardStage) => void, signal?: AbortSignal) => progressPost<WalkTurn>('/api/tech-wizard/tried', { ...t, lang, steps: steps.map((s) => ({ key: s.key, text: s.text })) }, stage, signal),
  headsUp: (appointmentId: string, lang: Lang) => apiFetch<HeadsUpView>(`/api/tech-wizard/heads-up/${encodeURIComponent(appointmentId)}?lang=${lang}`),
  headsUpAgain: (appointmentId: string, lang: Lang, stage?: (s: WizardStage) => void, signal?: AbortSignal) => progressPost<HeadsUpView>(`/api/tech-wizard/heads-up/${encodeURIComponent(appointmentId)}?lang=${lang}`, {}, stage, signal),
  freeHelp: (t: WizardTarget, help?: HelpKey | null) => apiFetch<FreeHelpView>(`/api/tech-wizard/free-help?${targetQuery(t)}${help ? `&help=${help}` : ''}`),
  steps: (t: WizardTarget) => apiFetch<{ followed: FollowedStep[] }>(`/api/tech-wizard/steps?${targetQuery(t)}`),
  checks: () => apiFetch<LibraryChecksView>('/api/tech-wizard/library/checks'),
  check: (id: string) => post<LibraryChecksView>('/api/tech-wizard/library/checks', { id }),
  reviewGuide: (id: string) => post<{ reviewedBy: string; reviewedAt: string }>('/api/tech-wizard/guides/review', { id }),
};
